export type LocalCandleWindow = {
  local_start_date: string;
  local_end_date: string;
  timezone: string;
};

function isoOrDateToMs(value: string | Date | null | undefined): number | null {
  if (value == null) return null;
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function compareCalendarDateStrings(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/** Calendar YMD for an instant in an IANA timezone (en-CA → YYYY-MM-DD). */
function formatDateAsCalendarDayInTimeZone(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: timeZone?.trim() || "UTC",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

/**
 * Expand a local YMD candle request so it covers every provided trade timestamp
 * (entry/exit). BE clips OHLC to the requested local window even when trade_ids
 * span earlier sessions (weekend-held metals, etc.).
 */
export function expandLocalCandleWindowToCoverTradeTimes(
  window: LocalCandleWindow,
  tradeTimes: Array<string | Date | null | undefined>
): LocalCandleWindow {
  const zone = window.timezone?.trim() || "UTC";
  let start = window.local_start_date;
  let end = window.local_end_date;

  for (const raw of tradeTimes) {
    const ms = isoOrDateToMs(raw);
    if (ms == null) continue;
    const ymd = formatDateAsCalendarDayInTimeZone(new Date(ms), zone);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) continue;
    if (compareCalendarDateStrings(ymd, start) < 0) start = ymd;
    if (compareCalendarDateStrings(ymd, end) > 0) end = ymd;
  }

  if (compareCalendarDateStrings(start, end) > 0) {
    return { ...window, local_start_date: end, local_end_date: start, timezone: zone };
  }
  return { ...window, local_start_date: start, local_end_date: end, timezone: zone };
}

/**
 * If BE reports a trade span that starts before the candle window we asked for,
 * expand local dates from span_start / span_end so a refetch includes those bars.
 */
export function expandLocalCandleWindowToCoverResolvedSpan(
  window: LocalCandleWindow,
  span: { span_start?: string | null; span_end?: string | null } | null | undefined
): LocalCandleWindow {
  if (!span) return window;
  return expandLocalCandleWindowToCoverTradeTimes(window, [span.span_start, span.span_end]);
}

/** Union two local windows (earliest start, latest end). */
export function mergeLocalCandleWindows(
  a: LocalCandleWindow,
  b: LocalCandleWindow
): LocalCandleWindow {
  const timezone = a.timezone?.trim() || b.timezone?.trim() || "UTC";
  let start = a.local_start_date;
  let end = a.local_end_date;
  if (compareCalendarDateStrings(b.local_start_date, start) < 0) start = b.local_start_date;
  if (compareCalendarDateStrings(b.local_end_date, end) > 0) end = b.local_end_date;
  if (compareCalendarDateStrings(start, end) > 0) {
    return { local_start_date: end, local_end_date: start, timezone };
  }
  return { local_start_date: start, local_end_date: end, timezone };
}

export function localCandleWindowsEqual(
  a: LocalCandleWindow | null | undefined,
  b: LocalCandleWindow | null | undefined
): boolean {
  if (!a || !b) return a === b;
  return (
    a.local_start_date === b.local_start_date &&
    a.local_end_date === b.local_end_date &&
    (a.timezone?.trim() || "UTC") === (b.timezone?.trim() || "UTC")
  );
}
